import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * PageLoader component - Centralized loading indicator for pages and sections
 *
 * Provides consistent loading states across the application with three variants:
 * - 'page': For full page content loading (default)
 * - 'section': For section/component loading with moderate padding
 * - 'viewport': For full viewport loading (auth gates, splash screens)
 *
 * @example
 * ```tsx
 * // Full page loading
 * if (isLoading) return <PageLoader />;
 *
 * // Section loading with message
 * if (isLoading) return <PageLoader variant="section" message="Loading users..." />;
 *
 * // Viewport loading (auth guard)
 * if (isLoading) return <PageLoader variant="viewport" />;
 * ```
 */

export interface PageLoaderProps {
  /**
   * Loader variant determining size and spacing
   * @default 'page'
   */
  variant?: 'page' | 'section' | 'viewport';

  /**
   * Optional loading message to display below spinner
   */
  message?: string;

  /**
   * Size of the CircularProgress spinner in pixels
   * @default 40 (page/section), 48 (viewport)
   */
  size?: number;

  /**
   * Color of the spinner
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'inherit';
}

export const PageLoader = ({
  variant = 'page',
  message,
  size,
  color = 'primary',
}: PageLoaderProps) => {
  // Determine default size based on variant
  const defaultSize = variant === 'viewport' ? 48 : 40;
  const spinnerSize = size ?? defaultSize;

  // Determine container styles based on variant
  const getContainerStyles = () => {
    switch (variant) {
      case 'viewport':
        return {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: 'background.default',
        };
      case 'section':
        return {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
        };
      case 'page':
      default:
        return {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
        };
    }
  };

  return (
    <Box sx={getContainerStyles()}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={spinnerSize} color={color} />
        {message && (
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
